import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Switch,
  Space,
  message,
  Popconfirm,
  Tag,
  Typography,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  YoutubeOutlined,
  GoogleOutlined,
} from '@ant-design/icons';
import api from '../../services/api';

const { Title } = Typography;
const { TextArea } = Input;

const HashtagManagement = ({ embedded = false }) => {
  const [hashtags, setHashtags] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingHashtag, setEditingHashtag] = useState(null);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchHashtags();
  }, []);

  const fetchHashtags = async () => {
    setLoading(true);
    try {
      const response = await api.get('/api/hashtags');
      setHashtags(response.data.data || []);
    } catch (error) {
      message.error('Failed to load hashtags');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingHashtag(null);
    form.resetFields();
    form.setFieldsValue({
      enabled_youtube: true,
      enabled_google: true,
      is_active: true,
    });
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingHashtag(record);
    form.setFieldsValue(record);
    setModalVisible(true);
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/api/hashtags/${id}`);
      message.success('Hashtag deleted successfully');
      fetchHashtags();
    } catch (error) {
      message.error('Failed to delete hashtag');
      console.error(error);
    }
  };

  const handleSubmit = async (values) => {
    try {
      if (editingHashtag) {
        await api.put(`/api/hashtags/${editingHashtag.id}`, values);
        message.success('Hashtag updated successfully');
      } else {
        await api.post('/api/hashtags', values);
        message.success('Hashtag created successfully');
      }
      setModalVisible(false);
      fetchHashtags();
    } catch (error) {
      message.error(error.response?.data?.message || 'Failed to save hashtag');
      console.error(error);
    }
  };

  const columns = [
    {
      title: 'Hashtag Info',
      key: 'hashtag_info',
      render: (_, record) => (
        <div style={{ minWidth: 300 }}>
          {/* Hashtag text */}
          <div style={{ display: 'flex', alignItems: 'start', gap: 8, marginBottom: 6 }}>
            <Tag color="blue" style={{ fontSize: '14px', marginTop: 2 }}>
              {record.text}
            </Tag>
          </div>

          {/* Description */}
          {record.description && (
            <div style={{ fontSize: '12px', color: '#666', marginBottom: 6 }}>
              {record.description}
            </div>
          )}

          {/* Platform tags */}
          <div>
            {record.enabled_youtube && (
              <Tag icon={<YoutubeOutlined />} color="red" style={{ marginRight: 4, marginBottom: 4 }}>
                YouTube
              </Tag>
            )}
            {record.enabled_google && (
              <Tag icon={<GoogleOutlined />} color="blue" style={{ marginBottom: 4 }}>
                Google
              </Tag>
            )}
          </div>
        </div>
      ),
      width: '40%',
    },
    {
      title: 'Status & Created',
      key: 'status_info',
      render: (_, record) => (
        <div style={{ minWidth: 140 }}>
          <div style={{ marginBottom: 8 }}>
            <Tag color={record.is_active ? 'green' : 'default'}>
              {record.is_active ? 'Active' : 'Inactive'}
            </Tag>
          </div>
          <div style={{ fontSize: '12px' }}>
            <div style={{ color: '#666', marginBottom: 2 }}>Created</div>
            <div style={{ fontWeight: 500 }}>
              {record.created_at ? new Date(record.created_at).toLocaleDateString('en-GB', {
                day: '2-digit',
                month: 'short',
                year: 'numeric'
              }) : '-'}
            </div>
          </div>
        </div>
      ),
      width: '180px',
    },
    {
      title: 'Aksi',
      key: 'actions',
      fixed: 'right',
      width: 100,
      render: (_, record) => (
        <Space direction="vertical" size={4} style={{ width: '100%' }}>
          <Button
            type="primary"
            icon={<EditOutlined />}
            size="small"
            onClick={() => handleEdit(record)}
            block
          >
            Edit
          </Button>
          <Popconfirm
            title="Are you sure you want to delete this hashtag?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              danger
              icon={<DeleteOutlined />}
              size="small"
              block
            >
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const content = (
    <>
      {!embedded && <Title level={3} style={{ margin: '0 0 20px 0' }}>Hashtag Management</Title>}
      <div className="filters-row" style={{ justifyContent: 'flex-end' }}>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleCreate}
        >
          Add Hashtag
        </Button>
      </div>

        <Table
          columns={columns}
          dataSource={hashtags}
          loading={loading}
          rowKey="id"
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} hashtags`,
          }}
        />

        <Modal
          title={editingHashtag ? 'Edit Hashtag' : 'Create Hashtag'}
          open={modalVisible}
          onCancel={() => setModalVisible(false)}
          onOk={() => form.submit()}
          width={600}
        >
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
          >
            <Form.Item
              name="text"
              label="Hashtag"
              rules={[{ required: true, message: 'Please enter hashtag' }]}
            >
              <Input
                placeholder="#ai-itera"
                prefix="#"
              />
            </Form.Item>

            <Form.Item
              name="description"
              label="Description"
            >
              <TextArea
                rows={3}
                placeholder="Description of this hashtag"
              />
            </Form.Item>

            <Form.Item
              name="enabled_youtube"
              label="Enable YouTube Scraping"
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>

            <Form.Item
              name="enabled_google"
              label="Enable Google Scraping"
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>

            <Form.Item
              name="is_active"
              label="Active"
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>
          </Form>
        </Modal>
    </>
  );

  if (embedded) {
    return content;
  }

  return (
    <Card>{content}</Card>
  );
};

export default HashtagManagement;
